/**
 * Turning an enquiry into a client.
 *
 * ===========================================================================
 * WHY THIS IS A REVIEW SCREEN AND NOT A BUTTON
 * ===========================================================================
 * One press creates a client, its contacts and its matters, moves the engagement from
 * the lead to the new client, and stamps both records with the link between them. That
 * is a lot to happen behind a word, and none of it is convenient to undo — a client
 * cannot be deleted in this product at all, because the firm's retention obligation
 * outlives the relationship.
 *
 * So the screen says what will exist afterwards, in the firm's own terms, before
 * anything does.
 *
 * ===========================================================================
 * AND WHY IT SHOWS THE REASONS IT CANNOT RUN
 * ===========================================================================
 * Conversion has real preconditions: a signed engagement, a service to open, a cleared
 * conflict check. Every one of them is something the person reading this screen can go
 * and fix.
 *
 * A greyed-out button with no explanation is indistinguishable from a broken one, and
 * the thing people do next is file a bug. So the server returns each blocker as a
 * sentence and they are all listed.
 */

import {
  ConflictError,
  STAFF,
  newIdempotencyKey,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  type ConversionPlan,
  type ConversionResult,
} from '@legalcrm/api';
import { Badge, Button, Modal, Spinner, useToast } from '@legalcrm/ui';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';

export function ConvertLeadDialog({
  leadId,
  open,
  onClose,
}: {
  leadId: string;
  open: boolean;
  onClose: () => void;
}) {
  const api = useApi();
  const navigate = useNavigate();
  const { toast } = useToast();

  /**
   * One key per attempt, reused across retries of that attempt.
   *
   * A fresh key on every click would defeat the point: the server is idempotent on this
   * value, which is what makes a double-submitted form safe. Held in a ref so a
   * re-render does not mint a second one.
   */
  const idempotencyKey = useRef<string | null>(null);

  const plan = useApiQuery<ConversionPlan>(
    qk.detail('lead-conversion-plan', leadId),
    STAFF.leadConversionPlan(leadId),
    { enabled: open },
  );

  const convert = useApiMutation<ConversionResult, void>(
    {
      mutationFn: () => {
        idempotencyKey.current ??= newIdempotencyKey();
        return api.post<ConversionResult>(STAFF.leadConvert(leadId), undefined, {
          idempotencyKey: idempotencyKey.current,
        });
      },
      // Leads, clients and cases all change. So does the lead's own detail, which now
      // carries the link to the client it became.
      invalidates: [qk.all('leads'), qk.all('clients'), qk.all('cases')],
    },
    {
      onSuccess: (result) => {
        toast({
          title: 'Client created',
          description: 'The enquiry and the client are linked. Opening the client now.',
          tone: 'success',
        });
        onClose();
        if (result.client) navigate(`/clients/${result.client}`);
      },
      onError: (error) => {
        // The guards run again on execution — this screen can be seconds stale, and an
        // agreement can be withdrawn between reading it and pressing the button. The
        // server's reason is written for the user, so it is shown as given.
        toast({
          title: 'Conversion was refused',
          description:
            error instanceof ConflictError
              ? error.detail
              : 'Nothing was created. Try again.',
          tone: 'warning',
        });
      },
    },
  );

  const data = plan.data;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Convert this enquiry into a client"
      description="Everything below is taken from the enquiry. Nothing needs typing again."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={convert.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!data?.can_convert}
            loading={convert.isPending}
            onClick={() => void convert.mutateAsync()}
          >
            Create the client
          </Button>
        </>
      }
    >
      {plan.isLoading && (
        <div className="flex items-center gap-2 text-sm text-ink-muted">
          <Spinner /> Working out what this would create…
        </div>
      )}

      {data && (
        <div className="flex flex-col gap-4">
          {data.blockers.length > 0 && <Blockers reasons={data.blockers} />}
          {data.can_convert && <WhatWillExist plan={data} />}
        </div>
      )}
    </Modal>
  );
}

/** Why it cannot run. Each one is something the reader can go and fix. */
function Blockers({ reasons }: { reasons: string[] }) {
  return (
    <div className="rounded-md border border-warning/25 bg-warning-subtle p-3">
      <p className="text-sm font-medium text-ink">
        {reasons.length === 1
          ? 'One thing is missing before this can be converted'
          : `${reasons.length} things are missing before this can be converted`}
      </p>
      <ul className="mt-2 flex list-disc flex-col gap-1 pl-5">
        {reasons.map((reason) => (
          <li key={reason} className="text-sm text-ink-muted">
            {reason}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * What will exist afterwards.
 *
 * Written as a list of records rather than a form, because there is nothing here to
 * decide — every value came from the enquiry. Presenting it as editable fields would
 * invite somebody to retype what is already correct, and a value retyped from one
 * screen onto another is a value that eventually differs between them.
 */
function WhatWillExist({ plan }: { plan: ConversionPlan }) {
  return (
    <div className="flex flex-col gap-3">
      <Row label="Client">
        <span className="text-ink">{plan.client.display_name}</span>
      </Row>

      <Row label="Primary contact">
        <span className="text-ink">{plan.client.contact.full_name}</span>
        <span className="block text-ink-muted">
          {plan.client.contact.email || 'No email address'}
          {plan.client.contact.phone ? ` · ${plan.client.contact.phone}` : ''}
        </span>
        {!plan.client.contact.email && (
          // Worth saying now rather than when they try to invite them. The portal
          // invitation is an email and nothing else; without an address there is
          // nowhere to send it.
          <span className="mt-1 block text-xs text-ink-subtle">
            Without an address this client cannot be invited to the portal. You can add
            one afterwards.
          </span>
        )}
      </Row>

      <Row label={plan.services.length === 1 ? 'Matter' : 'Matters'}>
        {plan.services.length === 0 ? (
          <span className="text-ink-muted">None</span>
        ) : (
          plan.services.map((service) => (
            <span key={service.id} className="block text-ink">
              {service.name}
            </span>
          ))
        )}
      </Row>

      {plan.agreement && (
        <Row label="Engagement">
          <span className="text-ink">{plan.agreement.title}</span>
          <span className="mt-0.5 block text-xs text-ink-subtle">
            Moves from the enquiry to the new client.
          </span>
        </Row>
      )}

      {plan.client.custom_fields_carried.length > 0 && (
        <Row label="Your fields carried across">
          <div className="flex flex-wrap gap-1">
            {plan.client.custom_fields_carried.map((key) => (
              <Badge key={key} tone="neutral" size="sm">
                {key}
              </Badge>
            ))}
          </div>
          <span className="mt-1 block text-xs text-ink-subtle">
            Fields you record on both enquiries and clients. The rest stay on the
            enquiry.
          </span>
        </Row>
      )}

      <p className="border-t border-line pt-3 text-xs text-ink-subtle">
        The enquiry is kept and linked to the client, so how this client arrived stays
        on the record.
      </p>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[10rem_1fr]">
      <dt className="text-sm text-ink-muted">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}
